import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";

export type UseCaseProp = {
  title: string;
  highlight: string;
  image: string;
  useCases: {
    title: string;
    description: string;
  }[];
};

export default function ProductUseCaseSection({
  title,
  highlight,
  useCases,
  image,
}: UseCaseProp) {
  const [before, after] = title.split(highlight);

  return (
    <section className="flex flex-col gap-10">
      <div className="flex justify-center flex-col gap-8">
        <p className="flex items-center justify-center">
          <span className="bg-[#E7D7FD] rounded-full px-3 py-1 text-[#8937F4] inline-block font-semibold">
            <span className="text-primary-900">USE CASES</span>{" "}
          </span>
        </p>
        <span className="md:text-[45px] text-2xl text-secondary-900 font-bold text-center md:mb-10">
          {before}
          <span className="bg-gradient-to-r from-[#4337cc] to-primary-900 bg-clip-text text-transparent">
            {highlight}
          </span>{" "}
          {after}
        </span>
      </div>
      <div className="relative mx-auto h-[600px] w-[600px] hidden md:flex">
        <svg width="600" height="600" className="absolute top-0 left-0">
          <circle
            cx="300"
            cy="300"
            r="298"
            fill="none"
            stroke="#4337cc"
            strokeWidth="2"
            strokeDasharray="10,20"
          />
        </svg>
        <Image
          priority
          src={image}
          alt="Products Hero Background Image"
          height={100}
          width={250}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 object-contain"
        />
        <div className="group w-[280px] rounded-2xl flex flex-col bg-[#FAFAFA] p-6 absolute z-20 right-0 gap-2 border-neutral-200 border">
          <span className="text-lg text-secondary-900 font-bold">
            {useCases[0].title}
          </span>
          <p
            className={clsx(
              "text-start text-base text-secondary-900 overflow-hidden text-ellipsis",
              "line-clamp-3 group-hover:line-clamp-none transition-all duration-300"
            )}
          >
            {useCases[0].description}
          </p>
        </div>
        <div className="group w-[280px] rounded-2xl flex flex-col bg-[#FAFAFA] p-6 absolute z-20 top-[60px] left-[-120px] gap-2 border-neutral-200 border">
          <span className="text-lg text-secondary-900 font-bold ">
            {useCases[1].title}
          </span>
          <p
            className={clsx(
              "text-start text-base text-secondary-900 overflow-hidden text-ellipsis",
              "line-clamp-3 group-hover:line-clamp-none transition-all duration-300"
            )}
          >
            {useCases[1].description}
          </p>
        </div>
        <div className="group w-[280px] rounded-2xl flex flex-col bg-[#FAFAFA] p-6 absolute z-20 top-1/2 right-[-120px] gap-2 border-neutral-200 border">
          <span className="text-lg text-secondary-900 font-bold ">
            {useCases[2].title}
          </span>
          <p
            className={clsx(
              "text-start text-base text-secondary-900 overflow-hidden text-ellipsis",
              "line-clamp-3 group-hover:line-clamp-none transition-all duration-300"
            )}
          >
            {useCases[2].description}
          </p>
        </div>
        <div className="group w-[280px] rounded-2xl flex flex-col bg-[#FAFAFA] p-6 absolute z-20 bottom-0 left-[-80px] gap-2 border-neutral-200 border">
          <span className="text-lg text-secondary-900 font-bold ">
            {useCases[3].title}
          </span>
          <p
            className={clsx(
              "text-start text-base text-secondary-900 overflow-hidden text-ellipsis",
              "line-clamp-3 group-hover:line-clamp-none transition-all duration-300"
            )}
          >
            {useCases[3].description}
          </p>
        </div>
      </div>
      <div className="relative gap-4 flex flex-col md:hidden">
        <Image
          priority
          src={image}
          alt="Products Hero Background Image"
          height={100}
          width={250}
          className=" object-contain mx-auto py-4"
        />
        <div className="w-full rounded-2xl flex flex-col bg-[#FAFAFA] p-6 border-neutral-200 border gap-2">
          <span className="text-lg text-secondary-900 font-bold ">
            {useCases[0].title}
          </span>
          <span className="text-base text-secondary-900 text-start">
            {useCases[0].description}
          </span>
        </div>
        <div className="w-full rounded-2xl flex flex-col bg-[#FAFAFA] p-6 border-neutral-200 border gap-2">
          <span className="text-lg text-secondary-900 font-bold ">
            {useCases[1].title}
          </span>
          <span className="text-base text-secondary-900 text-start">
            {useCases[1].description}
          </span>
        </div>
        <div className="w-full rounded-2xl flex flex-col bg-[#FAFAFA] p-6 border-neutral-200 border gap-2">
          <span className="text-lg text-secondary-900 font-bold ">
            {useCases[2].title}
          </span>
          <span className="text-base text-secondary-900 text-start">
            {useCases[2].description}
          </span>
        </div>
        <div className="w-full rounded-2xl flex flex-col bg-[#FAFAFA] p-6 border-neutral-200 border gap-2">
          <span className="text-lg text-secondary-900 font-bold ">
            {useCases[3].title}
          </span>
          <span className="text-base text-secondary-900 text-start">
            {useCases[3].description}
          </span>
        </div>
      </div>
    </section>
  );
}
